import React, {useCallback, useEffect, useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {Link, useParams} from "react-router-dom"
import {Box} from "scholastic-client-components/src/ui/Box/Box"
import {APIStatus} from "scholastic-client-components/src/types/APITypes"
import {authSelectors, authSlice} from "scholastic-client-components/src/slices/authSlice"
import {LoginRequest} from "scholastic-client-components/src/api/authAPI"
import {DisableFormElementsComponent} from "scholastic-client-components/src/features/DisableFormElementsComponent"
import {AuthPageTemplate, AuthStep} from "../../features/Auth/AuthPageTemplate/AuthPageTemplate"
import {ResetRequest} from "../../api/authAPI"
import {Policies} from "../../features/Policies/Policies"
import {useLoginAPI} from "../../hooks/api/useLoginAPI"
import {useRecoveryEmailAPI} from "../../hooks/api/useRecoveryEmailAPI"
import {useSaveNewPasswordAPI} from "../../hooks/api/useSaveNewPasswordAPI"
import {useUserResendConfirmEmailAPI} from "../DevicePage/hooks/useUserResendConfirmEmailAPI"
import {Heading} from "./Heading"
import {useUserResendTFAAPI} from "./useUserResendTFAAPI"
import css from "./AuthPage.module.scss"
import {AuthTOSModal} from "./AuthTOSModal"
import {useTranslation} from "react-i18next"
import {useChangeLanguageAPI} from "../../features/ChangeLanguage/useChangeLanguageAPI"
import {ChangeLanguage} from "../../features/ChangeLanguage/ChangeLanguage"
import {changeLanguageSelectors} from "../../slices/changeLanguageSlice"
import {Button} from "scholastic-client-components/src/ui/Button/Button"
import {AppPaths} from "../../lib/routing"
import {TSNWebsiteLink} from "../../features/TSNWebsiteLink/TSNWebsiteLink"

export const AuthPage = () => {
    const dispatch = useDispatch()
    const {t} = useTranslation(["auth_portal"])
    const [recoveryErrorMessage, setRecoveryErrorMessage] = useState<string>("")
    const [authStep, setAuthStep] = useState<AuthStep>(AuthStep.Login)

    const {token} = useParams<{token: string}>()

    useChangeLanguageAPI()
    const locales = useSelector(changeLanguageSelectors.getData)?.locales

    const login = useLoginAPI()
    const {status: recoveryStatus, sendRecoveryEmail, secondsLeft} = useRecoveryEmailAPI()
    const {status: savePasswordStatus, saveNewPassword} = useSaveNewPasswordAPI()
    const {
        resendConfirmEmail,
        status: resendConfirmStatus,
        timeLeft: resendConfirmTimeLeft,
    } = useUserResendConfirmEmailAPI()
    const {resendTFA, status: resendTFAStatus, timeLeft: resendTFATimeLeft} = useUserResendTFAAPI()
    const loginStatus = useSelector(authSelectors.getLoginStatus)
    const initialEmail = useSelector(authSelectors.getInitialEmail)
    const nonConfirmedAccount = useSelector(authSelectors.getNonConfirmedAccount)
    const request2fa = useSelector(authSelectors.getRequest2fa)
    const errorMessage = useSelector(authSelectors.getError)

    const applyAuthStep = useCallback(
        (authStep: AuthStep) => {
            dispatch(authSlice.actions.setError(""))
            setAuthStep(authStep)
        },
        [dispatch]
    )

    useEffect(() => {
        dispatch(authSlice.actions.setNonConfirmedAccount(false))
    }, [authStep, dispatch])

    useEffect(() => {
        if (token) {
            applyAuthStep(AuthStep.CreatePassword)
        }
    }, [applyAuthStep, token])

    useEffect(() => {
        setRecoveryErrorMessage("")
    }, [authStep, dispatch])

    useEffect(() => {
        if (recoveryStatus === APIStatus.Failure) {
            setRecoveryErrorMessage(t("auth_portal:auth_page.email_not_registered"))
        }
    }, [recoveryStatus, t])

    const handleLogin = (payload: LoginRequest) => {
        login(payload)
    }

    const handleRecoveryEmail = (payload: ResetRequest) => {
        sendRecoveryEmail({
            payload,
            onSuccess: () => applyAuthStep(AuthStep.SuccessResetPassword),
        })
    }

    const handlePasswordSave = (newPassword: string) => {
        saveNewPassword({
            payload: {
                password: newPassword,
                token,
            },
        })
    }

    const handleBackToLogin = useCallback(() => {
        dispatch(authSlice.actions.reset())
        setAuthStep(AuthStep.Login)
    }, [dispatch])

    const disableForm = loginStatus === APIStatus.Loading || recoveryStatus === APIStatus.Loading

    return (
        <DisableFormElementsComponent disabled={disableForm}>
            <div className={css.language}>
                <ChangeLanguage locales={locales} />
            </div>

            <Box
                flex={{justifyContent: "center", alignItems: "center", columnDirection: true}}
                propsStyles={{minHeight: "80vh"}}
            >
                <AuthPageTemplate
                    loadingReset={recoveryStatus === APIStatus.Loading}
                    loading={loginStatus === APIStatus.Loading}
                    savePasswordStatus={savePasswordStatus}
                    onLogin={handleLogin}
                    onReset={handleRecoveryEmail}
                    onPasswordSave={handlePasswordSave}
                    error={errorMessage}
                    errorReset={recoveryErrorMessage}
                    heading={
                        <Heading
                            authStep={authStep}
                            savePasswordStatus={savePasswordStatus}
                            request2fa={request2fa}
                        />
                    }
                    onChangeInput={() => {
                        dispatch(authSlice.actions.setError(""))
                        setRecoveryErrorMessage("")
                    }}
                    step={authStep}
                    setStep={applyAuthStep}
                    navigateToLogin={() => {
                        window.location.replace("/auth")
                    }}
                    initialEmail={initialEmail}
                    verificationWarning={{
                        nonConfirmedAccount,
                        loginStatus,
                        recoveryStatus,
                        resendStatus: resendConfirmStatus,
                        resendConfirmEmail,
                        timeLeft: resendConfirmTimeLeft,
                    }}
                    tfa={{
                        request2fa,
                        resendTFA,
                        loader: resendTFAStatus === APIStatus.Loading,
                        timeLeft: resendTFATimeLeft,
                    }}
                    policies={<Policies by="login" disabled={disableForm} />}
                    onBackToLogin={handleBackToLogin}
                    resetSecondsLeft={secondsLeft}
                />
                <div className={css.tsn}>
                    {authStep === AuthStep.Login && (
                        <Box mb={1.5}>
                            <Link to={AppPaths.LoginSSO}>
                                <Button
                                    className={css.loginSSO}
                                    data-test={"login-sso-button"}
                                >{t`auth_portal:auth_page.login_sso`}</Button>
                            </Link>
                        </Box>
                    )}
                    <TSNWebsiteLink />
                </div>
            </Box>
            <AuthTOSModal />
        </DisableFormElementsComponent>
    )
}
